import React, { useState, useEffect, Fragment, useMemo, useCallback } from "react";
import styled from "styled-components";
import Axios from "axios";
import CategoryEach from "./categoryEach";
import { Button1, CheckBoxInput } from "../../../common/Buttons";
import Treeselect from "../../../common/Treeselect";
import { FaPlus, FaMinus, FaCheck, FaTimes, FaTrash } from "react-icons/fa";
import { HiCursorClick } from "react-icons/hi";
import { MdEdit } from "react-icons/md";
import TextInputModal from "../../../common/TextInputModal";
import { ClassicButton } from "../../../common/ButtonWithLoading";
import { Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { onFilterUpdateWithUser, onFilterUpdateWithMainUser } from "../../../../redux/Actions/filtersAction";
import useFetch from "../../../../hooks/useFetch";
import { getShopCategories } from "../../../../redux/Actions/shopCategoriesAction";
import { antdMessageSuccess } from "../../../../helpers/Utils";

const checkboxMassFunction = (array, correctArray, control) => {
  let newObj = correctArray;
  array.forEach((element) => {
    const currentIndex = newObj.findIndex(item =>
      item.id === element.id && item.identifier === element.identifier
    );

    if (currentIndex === -1 && control === "add") {
      newObj.push({
        id: element.id,
        identifier: element.identifier
      });
    }
    if (currentIndex !== -1 && control === "remove") {
      newObj.splice(currentIndex, 1);
    }

    if (element.children.length > 0) {
      return checkboxMassFunction(element.children, newObj, control);
    }
  });
  return newObj;
};

const findAllMatchingCategories = (allCategories, categoryId) => {
  if (!categoryId) return [];

  const matches = [];

  const traverse = (categories) => {
    categories.forEach(category => {
      if (category.id === categoryId) {
        matches.push({
          id: category.id,
          identifier: category.identifier,
          children: category.children || []
        });
      }
      if (category.children && category.children.length > 0) {
        traverse(category.children);
      }
    });
  };

  traverse(allCategories);
  return matches;
};

const convertFiltersForCheckbox = (apiFilters, allCategories) => {
  if (!Array.isArray(apiFilters) || !allCategories || allCategories.length === 0) return apiFilters;
  const result = [];
  apiFilters.forEach(filter => {
    const id = Array.isArray(filter) ? filter[0] : (typeof filter === 'object' ? filter.id : filter);
    const matches = findAllMatchingCategories(allCategories, id);
    matches.forEach(match => {
      if (!result.some(r => r.id === match.id && r.identifier === match.identifier)) {
        result.push({ id: match.id, identifier: match.identifier });
      }
    });
  });
  return result;
};

const getCategoryIdentityKey = (category) => `${category.id}::${category.identifier || ""}`;

const getCategoryTreeKey = (category, treePath) => `${treePath}::${getCategoryIdentityKey(category)}`;

const convertCategoriesToTreeOptions = (categories = [], parentPath = "") => {
  return categories.map((category, index) => {
    const treePath = parentPath ? `${parentPath}.${index}` : String(index);
    const treeKey = getCategoryTreeKey(category, treePath);

    return {
      title: category.title,
      value: treeKey,
      key: treeKey,
      children: convertCategoriesToTreeOptions(category.children || [], treePath),
    };
  });
};

const buildCategoryMaps = (categories = [], maps = { byKey: new Map(), keysByCategoryId: new Map() }, parentPath = "") => {
  categories.forEach((category, index) => {
    const treePath = parentPath ? `${parentPath}.${index}` : String(index);
    const treeKey = getCategoryTreeKey(category, treePath);

    maps.byKey.set(treeKey, {
      id: category.id,
      identifier: category.identifier,
      node: category,
    });

    // Group by id only (across all shops/identifiers)
    const idKey = String(category.id);
    const idKeys = maps.keysByCategoryId.get(idKey) || [];
    idKeys.push(treeKey);
    maps.keysByCategoryId.set(idKey, idKeys);

    if (category.children && category.children.length > 0) {
      buildCategoryMaps(category.children, maps, treePath);
    }
  });

  return maps;
};

const mapFiltersToTreeKeys = (filters = [], keysByCategoryId = new Map()) => {
  const result = [];
  const added = new Set();

  filters.forEach((filter) => {
    // Handle both plain ID (from backend after refresh) and object {id, identifier}
    const idKey = String(typeof filter === 'object' && filter !== null ? filter.id : filter);
    const allTreeKeys = keysByCategoryId.get(idKey) || [];
    allTreeKeys.forEach((treeKey) => {
      if (!added.has(treeKey)) {
        added.add(treeKey);
        result.push(treeKey);
      }
    });
  });

  return result;
};

const FavoriteTreeselect = ({ options, filters, onChange }) => {
  const treeOptions = useMemo(() => convertCategoriesToTreeOptions(options), [options]);
  const maps = useMemo(() => buildCategoryMaps(options), [options]);

  const filtersToKeys = useCallback(
    (f) => mapFiltersToTreeKeys(f, maps.keysByCategoryId),
    [maps.keysByCategoryId]
  );

  const [localKeys, setLocalKeys] = useState(() => filtersToKeys(filters));

  useEffect(() => {
    setLocalKeys(filtersToKeys(filters));
  }, [filters, filtersToKeys]);

  const handleChange = useCallback((values) => {
    const selectedValues = new Set(Array.isArray(values) ? values : values != null ? [values] : []);
    const prevKeys = new Set(localKeys);

    // Find which category IDs were removed/added (by comparing tree keys)
    const removedIds = new Set();
    prevKeys.forEach((treeKey) => {
      if (!selectedValues.has(treeKey)) {
        const entry = maps.byKey.get(treeKey);
        if (entry) removedIds.add(String(entry.id));
      }
    });

    const addedIds = new Set();
    selectedValues.forEach((treeKey) => {
      if (!prevKeys.has(treeKey)) {
        const entry = maps.byKey.get(String(treeKey));
        if (entry) addedIds.add(String(entry.id));
      }
    });

    // Build the final set of category IDs
    const currentIds = new Set();
    prevKeys.forEach((treeKey) => {
      const entry = maps.byKey.get(treeKey);
      if (entry) currentIds.add(String(entry.id));
    });
    removedIds.forEach((id) => currentIds.delete(id));
    addedIds.forEach((id) => currentIds.add(id));

    // Expand IDs back to ALL treeKeys across all shops (matching old behavior)
    const newKeys = [];
    const result = [];
    const seen = new Set();
    currentIds.forEach((idKey) => {
      const allTreeKeys = maps.keysByCategoryId.get(idKey) || [];
      allTreeKeys.forEach((treeKey) => {
        newKeys.push(treeKey);
        const entry = maps.byKey.get(treeKey);
        if (!entry) return;
        const identityKey = `${entry.id}::${entry.identifier || ""}`;
        if (!seen.has(identityKey)) {
          seen.add(identityKey);
          result.push({ id: entry.id, identifier: entry.identifier });
        }
      });
    });

    setLocalKeys(newKeys);
    onChange(result);
  }, [maps, localKeys, onChange]);

  return (
    <Treeselect
      options={treeOptions}
      value={localKeys}
      onChange={handleChange}
      placeholder="Select categories..."
      multiSelect={true}
      searchable={true}
      dropdownWidth="100%"
      showStrategy="SHOW_ALL"
      className="category-filter-treeselect"
    />
  );
};

function CategoryFilterModal() {
  const dispatch = useDispatch();
  const categoryFilter = useSelector(
    (state) => state.filters.orders.categoryFilter
  );
  const user = useSelector((state) => state.auth.user);
  const [Category, setCategory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getShopCategories(false).then((res) => {
      setLoading(false);
      if (res.success) {
        setCategory(res.data);
      } else {
        setCategory([]);
      }
    });
  }, []);

  const onUpdateUser = useCallback((e) => {
    dispatch(onFilterUpdateWithUser(e, "user_categories", "categoryFilter"));
  }, [dispatch]);

  const onUpdateMainUser = useCallback((e) => {
    dispatch(onFilterUpdateWithMainUser(e, "user_categories", "categoryFilter"));
  }, [dispatch]);

  return (
    <div className="row">
      <div className="col">
        <div>
          <Divs className="container-title-block">
            <MainBlock className="d-flex justify-content-between align-items-center">
              <span>Choices</span>
              <span>Selected: {categoryFilter.length}</span>
            </MainBlock>
            {loading ? (
              <div className="text-center my-4">
                <Spin />
              </div>
            ) : (
              <Fragment>
                <FavoriteTreeselect
                  options={Category}
                  filters={categoryFilter}
                  onChange={onUpdateUser}
                />
                <SetFavoritesCategories
                  tableColumnSelector={onUpdateMainUser}
                  options={Category}
                />
              </Fragment>
            )}
          </Divs>

          {user.role == "admin" && (
            <Divs>
              <OtherPeopleFilters tableColumnSelector={onUpdateMainUser} />
            </Divs>
          )}
        </div>
      </div>
    </div>
  );
}

const getParentIdsOfChecked = (categories, checkedSet) => {
  const parentIds = [];
  const traverse = (items) => {
    items.forEach(item => {
      if (item.children && item.children.length > 0) {
        const hasCheckedDescendant = (children) => {
          return children.some(child => {
            if (checkedSet.has(`${child.id}-${child.identifier}`)) return true;
            if (child.children && child.children.length > 0) return hasCheckedDescendant(child.children);
            return false;
          });
        };
        if (hasCheckedDescendant(item.children)) {
          parentIds.push(item.id);
        }
        traverse(item.children);
      }
    });
  };
  traverse(categories);
  return parentIds;
};

const hiddenStyle = { visibility: "hidden" };
const visibleStyle = { visibility: "visible" };
const cursorStyle = { cursor: "pointer" };

const CategoriesList = React.memo(({ Category, handleCheckboxChange, categoryFilter }) => {
  const [CurrentOpen, setCurrentOpen] = useState([]);
  const manuallyClosed = React.useRef(new Set());

  const currentOpenSet = useMemo(() => new Set(CurrentOpen), [CurrentOpen]);
  const checkedSet = useMemo(
    () => new Set(categoryFilter.map(f => `${f.id}-${f.identifier}`)),
    [categoryFilter]
  );

  useEffect(() => {
    if (Category.length > 0 && categoryFilter.length > 0) {
      const parentIds = getParentIdsOfChecked(Category, checkedSet);
      setCurrentOpen(prev => {
        const prevSet = new Set(prev);
        const newIds = parentIds.filter(id => !prevSet.has(id) && !manuallyClosed.current.has(id));
        if (newIds.length === 0) return prev;
        return [...prev, ...newIds];
      });
    }
  }, [Category, checkedSet]);

  const handleToggle = useCallback((value) => {
    setCurrentOpen(prev => {
      const currentIndex = prev.indexOf(value);
      if (currentIndex === -1) {
        manuallyClosed.current.delete(value);
        return [...prev, value];
      } else {
        manuallyClosed.current.add(value);
        const newArr = [...prev];
        newArr.splice(currentIndex, 1);
        return newArr;
      }
    });
  }, []);

  return (
    <ul className="list-group">
      {Category.map((item) => {
        const lengthItems = item.children.length > 0;
        const isOpen = currentOpenSet.has(item.id);
        const isChecked = checkedSet.has(`${item.id}-${item.identifier}`);

        return (
          <React.Fragment key={`${item.id}-${item.identifier}`}>
            <li className="list-group-item node-treeview mb-2 d-flex justify-content-between">
              <div
                style={cursorStyle}
                onClick={() => handleToggle(item.id)}
              >
                {isOpen ? (
                  <FaMinus
                    title="Close"
                    className="mr-2 text-danger"
                    style={lengthItems ? visibleStyle : hiddenStyle}
                  />
                ) : (
                  <FaPlus
                    title="Open"
                    className="mr-2 text-success"
                    style={lengthItems ? visibleStyle : hiddenStyle}
                  />
                )}
                {item.title}{" "}
                {lengthItems && (
                  <span className="font-weight-bold">
                    ({item.children.length})
                  </span>
                )}
              </div>
              <div>
                <CheckBoxInput
                  type="checkbox"
                  color="#f7951e"
                  checked={isChecked}
                  onChange={() => handleCheckboxChange(item, item.children)}
                />
              </div>
            </li>
            {isOpen &&
              lengthItems &&
              item.children.map((child) => (
                <CategoryEach
                  key={`${child.id}-${child.identifier}`}
                  item={child}
                  checkedSet={checkedSet}
                  handleCheckboxChange={handleCheckboxChange}
                  currentOpenSet={currentOpenSet}
                  handleToggle={handleToggle}
                  level={7}
                />
              ))}
          </React.Fragment>
        );
      })}
    </ul>
  );
});

const SetFavoritesCategories = ({ options = [], tableColumnSelector }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const { data } = await Axios.get("/api/v1/user_categories_favorites");
        if (data) {
          setFavoritedFilters(data);
          return setLoading(false);
        }
        throw new Error("Err");
      } catch (err) {
        console.log("Error: ", err);
        setLoading(false);
      }
    };
    getCategories();
  }, []);

  const createNewFilter = async (params) => {
    try {
      const transformedParams = {
        title: params.title,
        filters: params.filters.map(filter => filter.id)
      };

      const { data } = await Axios.post("/api/v1/user_categories_favorites", {
        user_categories_favorite: transformedParams,
      });
      if (data) {
        setFavoritedFilters([...favoritedFilters, data]);
        setNewCategories(false);
      }
    } catch (err) {
      console.log("Error: ", err);
    }
  };

  const updateFilter = async (params) => {
    try {
      const transformedParams = {
        id: params.id,
        filters: params.filters.map(filter => filter.id)
      };

      const { data } = await Axios.put(
        `/api/v1/user_categories_favorites/${params.id}`,
        {
          user_categories_favorite: transformedParams,
        }
      );
      if (data) {
        const allFavorites = [...favoritedFilters];
        const findIndex = allFavorites.findIndex((e) => e.id == params.id);
        if (findIndex !== -1) {
          allFavorites[findIndex] = data;
          setFavoritedFilters(allFavorites);
          setNewCategories(false);
          return true;
        } else {
          console.log("Klaida");
          setNewCategories(false);
        }
      }
    } catch (err) {
      console.log("Error: ", err);
    }
  };
  const removeFilter = async (id) => {
    try {
      if (!confirm("Are you sure want to delete?")) return;
      const { data } = await Axios.delete(
        `/api/v1/user_categories_favorites/${id}`

      );
      if (data.success) {
        setFavoritedFilters(favoritedFilters.filter((e) => e.id !== id));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const [favoritedFilters, setFavoritedFilters] = useState([]);

  const [newCategories, setNewCategories] = useState(false);

  return (
    <Fragment>
      <p className="font-weight-bold mb-1 mt-4">Favorite filters</p>

      <div className="">
        {loading ? (
          <div className="text-center my-4">
            <Spin />
          </div>
        ) : (
          favoritedFilters.map((e) => (
            <FavoritedFilter
              key={e.id}
              {...e}
              selectFilter={(val) => tableColumnSelector(val)}
              options={options}
              updateFilter={updateFilter}
              removeFilter={removeFilter}
            />
          ))
        )}
      </div>
      {!loading && newCategories && (
        <CreateFilter options={options} createNewFilter={createNewFilter} />
      )}
      {!loading && favoritedFilters.length < 15 && (
        <div className="position-relative mt-5">
          <Button1
            style={{
              position: "absolute",
              top: "-16px",
              left: "50%",
              transform: "translate(-50%, 0px)",
            }}
            text={newCategories ? "Cancel" : "Add a new categories"}
            option={newCategories ? "minus" : "plus"}
            onClick={() => setNewCategories(!newCategories)}
          />
          <hr />
        </div>
      )}
    </Fragment>
  );
};

function FavoritedFilter({
  id,
  title,
  filters,
  selectFilter,
  options,
  updateFilter,
  removeFilter,
}) {
  const [edit, setEdit] = useState(false);
  const [editFilters, setEditFilters] = useState(filters);

  const renderFilters = () => {
    if (!Array.isArray(filters)) return null;

    return (
      <Fragment>
        {filters.slice(0, 5).map((filter, idx) => {
          const [filterId, filterTitle] = Array.isArray(filter) ? filter : [filter.id, filter.title];
          return (
            <span key={filterId}>
              {filterTitle}
              {idx < Math.min(filters.length, 5) - 1 && ", "}
              {idx === 4 && filters.length > 5 && <b>...</b>}
            </span>
          );
        })}
        <div>
          <b>Total in filter: {filters.length}</b>
        </div>
      </Fragment>
    );
  };

  return (
    <div className="mb-2 pl-2" style={{ borderLeft: "2px solid #f7951e" }}>
      <div className="d-flex justify-content-between mb-2 pb-1 border-bottom border-muted">
        {edit ? (
          <span>{title}</span>
        ) : (
          <div
            className="cursor-pointer expand-hover"
            onClick={() => {
              selectFilter(convertFiltersForCheckbox(filters, options));
              antdMessageSuccess(`Category filter ${title} assigned`);
            }}
          >
            <HiCursorClick
              size={17}
              className="text-warning mr-2"
              title="Choose"
            />
            <span>{title}</span>
          </div>
        )}
        {updateFilter && (
          <>
            {edit ? (
              <span>
                <FaCheck
                  size={17}
                  className="text-success cursor-pointer"
                  onClick={async () => {
                    const success = await updateFilter({
                      id,
                      filters: editFilters,
                    });
                    if (success) {
                      setEdit(false);
                    }
                  }}
                />
                <FaTimes
                  size={17}
                  className="text-danger cursor-pointer ml-2"
                  onClick={() => {
                    setEditFilters(convertFiltersForCheckbox(filters, options));
                    setEdit(false);
                  }}
                />
              </span>
            ) : (
              <span>
                <MdEdit
                  size={17}
                  className="cursor-pointer"
                  color="green"
                  onClick={() => {
                    setEditFilters(convertFiltersForCheckbox(filters, options));
                    setEdit(true);
                  }}
                />

                <FaTrash
                  className="cursor-pointer text-danger ml-2"
                  size={17}
                  onClick={() => removeFilter(id)}
                />
              </span>
            )}
          </>
        )}
      </div>
      {edit ? (
        <FavoriteTreeselect
          options={options}
          filters={editFilters}
          onChange={setEditFilters}
        />
      ) : renderFilters()}
    </div>
  );
}

function CreateFilter({ options, createNewFilter }) {
  const [newFF, setNewFF] = useState({
    title: "",
    filters: [],
  });

  const [errors, setErrors] = useState({});
  const createItem = () => {
    if (newFF.title.trim().length === 0 || newFF.filters.length === 0) {
      let errors = {};
      if (newFF.title.trim().length === 0) errors.title = "Enter a name";
      if (newFF.filters.length === 0) errors.filters = "Select categories";

      return setErrors(errors);
    }

    createNewFilter(newFF);
  };

  return (
    <div>
      <div className="d-flex justify-content-between flex-column mt-3">
        <p className="font-weight-bold mb-1">Creating a new category filter</p>
        <PretendDiv className="mb-3">
          <TextInputModal
            required="true"
            text="Title"
            placeholder="Enter"
            name="title"
            value={newFF.title}
            onChange={(e) => setNewFF({ ...newFF, title: e.target.value })}
            warning={errors.title}
          />
        </PretendDiv>
        <PretendDiv>
          <FavoriteTreeselect
            options={options}
            filters={newFF.filters}
            onChange={(filters) => setNewFF({ ...newFF, filters })}
          />
        </PretendDiv>
        {errors.filters && <div className="text-danger">{errors.filters}</div>}
      </div>
      <div className="d-flex justify-content-end mt-2">
        <ClassicButton
          loading={false}
          text={"Create"}
          onClick={() => createItem()}
        />
      </div>
    </div>
  );
}

const OtherPeopleFilters = ({ tableColumnSelector }) => {
  const { value, error, loading } = useFetch(
    "/api/v1/user_categories_favorites/other_people_filters"
  );

  return (
    <div>
      <p className="font-weight-bold mb-1 mt-4">
        Other persons favorite filters
      </p>

      <div className="">
        {error ? (
          <div className="text-center my-4">
            Something wrong: {JSON.stringify(error)}{" "}
          </div>
        ) : loading ? (
          <div className="text-center my-4">
            <Spin />
          </div>
        ) : (
          Object.entries(value.data).map((e) => (
            <div key={e[0]}>
              <div className="text-sm mb-1 mt-3">{e[0]}</div>
              {e[1].map((d) => (
                <FavoritedFilter
                  key={d.id}
                  {...d}
                  selectFilter={(val) => tableColumnSelector(val)}
                  options={[]}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const PretendDiv = styled.div`
  color: #000000;
  /* flex: 1; */
  width: 100%;
  & .text-bold {
    font-weight: 700;
  }
`;

const MainBlock = styled.div`
  background-color: #f2f2f2;
  padding: 8px 10px;
  display: block;
  font-weight: 700;
  color: #000000;
  font-size: 10px;
`;

const Divs = styled.div`
  margin: 6px 0 !important;
  display: block;
  color: #000000;
  font-size: 10px;
  line-height: 14px;
  font-weight: 500;
  & .category-filter-treeselect .ant-select-selector {
    max-height: 120px;
    overflow-y: auto !important;
  }
  & .text-bold {
    font-weight: 700;
  }
  & .form-input {
    display: block;
    width: 100%;
    height: calc(1.5em + 0.75rem + 0.5px);
    padding: 0.1rem 0.2rem;
    font-size: 0.6rem;
    border: 1px solid silver;
    background-color: transparent;
    color: black;
    border-radius: 0.25rem;
    &:active {
      border: 1px solid #f7951e !important;
      border-radius: 0.25rem !important;
    }
    &:focus {
      border: 1px solid #f7951e !important;
      border-radius: 0.25rem !important;
      & input {
        outline: none !important;
      }
    }
  }
  & .orange_color {
    color: #f7951e;
  }
`;

export default CategoryFilterModal;
